import { useState } from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { fetchRoles } from "../../../Redux/Slices/permissionsSlice";
import { useAppDispatch } from "../../../Redux/hooks";
import UnifiedLoginForm from "./UnifiedLoginForm";
import EmailLoginForm from "./EmailLoginForm";
import MobilePasswordLoginForm from "./MobilePasswordLoginForm";
import {
  isPartnerAuthLayout,
  isPasswordLogin,
} from "../../../config/sellerOrgConfig";
import { PARTNER_PRIMARY_HEX } from "../../../constants/partnerBrand";
import { useSellerOrgConfig } from "../../../Hooks/useSellerOrgConfig";

export interface SignInFormProps {
  /** @deprecated Layout is resolved via seller org config; prop is ignored. */
  embeddedInPartnerLayout?: boolean;
}

const PARTNER_PRIMARY = PARTNER_PRIMARY_HEX;

const SignInForm: React.FC<SignInFormProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const orgConfig = useSellerOrgConfig();
  const embeddedInPartnerLayout = isPartnerAuthLayout(orgConfig);

  // For password-based orgs (e.g. "valar"): toggle between email and mobile login
  const [loginMode, setLoginMode] = useState<"email" | "mobile">("mobile");

  const handleLoginSuccess = async (response: any) => {
    const { access_token, refresh_token } = response?.data?.data || {};
    if (access_token && refresh_token) {
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.removeItem("LOGOUT_STATUS");

      const profileResult = await dispatch(fetchRoles());
      if (fetchRoles.rejected.match(profileResult)) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("LOGIN_STATUS");
        return;
      }

      localStorage.setItem("LOGIN_STATUS", "LOGIN");
      
      // ✅ Request notification permission immediately after login (if not already requested)
      // This ensures permission is requested early for better cross-browser compatibility
      if ('Notification' in window && Notification.permission === 'default') {
        try {
          const permission = await Notification.requestPermission();
          console.log('[SignIn] Notification permission requested:', permission);
          if (permission === 'granted') {
            console.log('[SignIn] Notification permission granted');
          } else if (permission === 'denied') {
            console.warn('[SignIn] Notification permission denied - user must enable in browser settings');
          }
        } catch (error) {
          console.error('[SignIn] Error requesting notification permission:', error);
          // Continue with login flow even if permission request fails
        }
      }
      
      setTimeout(() => {
        // Partner portal login layout applies a global scroll lock (html/body overflow: hidden).
        // Ensure it's released before navigating so the dashboard can scroll immediately.
        const html = document.documentElement;
        const body = document.body;
        html.style.overflow = "";
        body.style.overflow = "";
        html.style.height = "";
        body.style.height = "";

        localStorage.setItem("lastPath", "/dashboard");
        navigate("/dashboard", { replace: true });
      }, 100);
    }
  };

  const isPasswordLoginMode = isPasswordLogin(orgConfig);
  const { copy } = orgConfig.auth;

  return (
    <Box>
      {embeddedInPartnerLayout ? (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
              fontWeight: 800,
              fontSize: "1.75rem",
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            {copy.loginTitle}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "rgba(21, 28, 39, 0.65)", lineHeight: 1.5 }}>
            {copy.loginSubtitle}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              textWrap: "pretty",
              fontWeight: 700,
              fontSize: "clamp(18px, 2vw, 24px)",
              lineHeight: "150%",
              textAlign: "center",
            }}
          >
            {copy.loginTitle}
          </Typography>
          <Typography
            sx={{
              mt: { xs: 0.5, sm: 1 },
              textWrap: "pretty",
              fontSize: "clamp(10px, 1.5vw, 13px)",
              fontWeight: 500,
              textAlign: "center",
              lineHeight: "150%",
              color: "rgba(0, 0, 0, 0.5)",
            }}
          >
            {copy.loginSubtitle}
          </Typography>
        </Box>
      )}

      {/* Password-based login for "valar" org code */}
      {/* OTP-based login for "tazty" and all other org codes */}
      {isPasswordLoginMode ? (
        loginMode === "email" ? (
          <EmailLoginForm
            onSuccess={handleLoginSuccess}
            onSwitchToMobile={() => setLoginMode("mobile")}
          />
        ) : (
          <MobilePasswordLoginForm
            onSuccess={handleLoginSuccess}
            onSwitchToEmail={() => setLoginMode("email")}
          />
        )
      ) : (
        <UnifiedLoginForm
          onSuccess={handleLoginSuccess}
          partnerVisual={embeddedInPartnerLayout}
        />
      )}

      {embeddedInPartnerLayout && !isPasswordLoginMode && (
        <Box
          sx={{
            mt: 3,
            pt: 3,
            borderTop: "1px solid rgba(188,203,185,0.2)",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            sx={{
              bgcolor: `${PARTNER_PRIMARY}0d`,
              p: 2,
              borderRadius: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              }}
            >
              <LockOutlinedIcon sx={{ color: PARTNER_PRIMARY, fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontSize: "0.75rem", color: "rgba(21, 28, 39, 0.7)", lineHeight: 1.5 }}>
              We&apos;ll send a 4-digit OTP for secure access. No passwords required.
            </Typography>
          </Stack>
        </Box>
      )}

      <Box sx={{ textAlign: "center", mt: embeddedInPartnerLayout ? 2 : "10px" }}>
        <Typography
          align="center"
          sx={{
            textAlign: "center",
            color: "rgba(0, 0, 0, 0.5)",
            fontWeight: 400,
            lineHeight: "150%",
            fontSize: embeddedInPartnerLayout ? "0.75rem" : "clamp(10px, 1.3vw, 11px)",
          }}
        >
          Don&apos;t have an account yet?{" "}
          <Link
            href="/registration"
            variant="caption"
            sx={{
              "&::before": { display: "none" },
              ml: 0.25,
              fontWeight: 700,
              color: embeddedInPartnerLayout ? PARTNER_PRIMARY : undefined,
            }}
          >
            {copy.registerLinkLabel}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default SignInForm;
